import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiService from '../../../../services/apiService';

interface Location {
  id: string;
  locationCode: string;
  locationName: string;
  locationType: string;
  level: number;
  capacity?: number;
  usedCapacity?: number;
  children?: Location[];
  parentId?: string;
}

interface Warehouse {
  id: string;
  name: string;
}

const locationTypes = [
  { value: 'ZONE', label: 'Zone' },
  { value: 'AISLE', label: 'Aisle' },
  { value: 'RACK', label: 'Rack' },
  { value: 'SHELF', label: 'Shelf' },
  { value: 'BIN', label: 'Bin' },
];

const WarehouseLocationManagement: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [companyId, setCompanyId] = useState<string>('comp_001'); // Default company ID

  // Form state
  const [formData, setFormData] = useState({
    locationCode: '',
    locationName: '',
    locationType: 'ZONE',
    parentId: '',
    level: 1,
    capacity: 0,
  });

  // Load warehouses on component mount
  useEffect(() => {
    fetchWarehouses();
  }, []);

  // Load locations when warehouse is selected
  useEffect(() => {
    if (selectedWarehouse) {
      fetchLocations();
    }
  }, [selectedWarehouse]);

  const fetchWarehouses = async () => {
    try {
      const response = await apiService.get(`/api/v1/warehouses?companyId=${companyId}`);
      if (response.data.success) {
        setWarehouses(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedWarehouse(response.data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      toast.error(t('Failed to fetch warehouses'));
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await apiService.get(
        `/api/v1/warehouse-management/locations/hierarchy?warehouseId=${selectedWarehouse}&companyId=${companyId}`
      );
      if (response.data.success) {
        setLocations(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error(t('Failed to fetch warehouse locations'));
    }
  };

  const handleOpenDialog = (parentLocation?: Location) => {
    setIsEditing(false);
    setFormData({
      locationCode: '',
      locationName: '',
      locationType: parentLocation ? getNextLocationType(parentLocation.locationType) : 'ZONE',
      parentId: parentLocation ? parentLocation.id : '',
      level: parentLocation ? parentLocation.level + 1 : 1,
      capacity: 0,
    });
    setOpenDialog(true);
  };

  const handleEditLocation = (location: Location) => {
    setIsEditing(true);
    setSelectedLocation(location);
    setFormData({
      locationCode: location.locationCode,
      locationName: location.locationName,
      locationType: location.locationType,
      parentId: location.parentId || '',
      level: location.level,
      capacity: location.capacity || 0,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLocation(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        warehouseId: selectedWarehouse,
        companyId,
      };

      if (isEditing && selectedLocation) {
        // Update existing location
        const response = await apiService.put(`/api/v1/warehouse-management/locations/${selectedLocation.id}`, payload);
        if (response.data.success) {
          toast.success(t('Location updated successfully'));
          fetchLocations();
          handleCloseDialog();
        }
      } else {
        // Create new location
        const response = await apiService.post('/api/v1/warehouse-management/locations', payload);
        if (response.data.success) {
          toast.success(t('Location created successfully'));
          fetchLocations();
          handleCloseDialog();
        }
      }
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error(t('Failed to save location'));
    }
  };

  const handleDeleteLocation = async (location: Location) => {
    if (window.confirm(t('Are you sure you want to delete this location?'))) {
      try {
        const response = await apiService.delete(`/api/v1/warehouse-management/locations/${location.id}`);
        if (response.data.success) {
          toast.success(t('Location deleted successfully'));
          fetchLocations();
        }
      } catch (error) {
        console.error('Error deleting location:', error);
        toast.error(t('Failed to delete location'));
      }
    }
  };

  const getNextLocationType = (currentType: string): string => {
    const types = locationTypes.map(t => t.value);
    const currentIndex = types.indexOf(currentType);
    return currentIndex < types.length - 1 ? types[currentIndex + 1] : types[types.length - 1];
  };

  const renderTree = (nodes: Location[]) => (
    nodes.map((node) => (
      <TreeItem
        key={node.id}
        nodeId={node.id}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">
                {node.locationCode} - {node.locationName} ({node.locationType})
              </Typography>
            </Box>
            <Box>
              <IconButton size="small" onClick={() => handleOpenDialog(node)}>
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleEditLocation(node)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => handleDeleteLocation(node)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        }
      >
        {Array.isArray(node.children) && node.children.length > 0
          ? renderTree(node.children)
          : null}
      </TreeItem>
    ))
  );

  return (
    <Card>
      <CardHeader
        title={t('Warehouse Location Management')}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={!selectedWarehouse}
          >
            {t('Add Top Level Location')}
          </Button>
        }
      />
      <Divider />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="warehouse-select-label">{t('Warehouse')}</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={selectedWarehouse}
                label={t('Warehouse')}
                onChange={(e) => setSelectedWarehouse(e.target.value as string)}
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                minHeight: 400,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {locations.length > 0 ? (
                <TreeView
                  defaultCollapseIcon={<ExpandMoreIcon />}
                  defaultExpandIcon={<ChevronRightIcon />}
                  sx={{ flexGrow: 1 }}
                >
                  {renderTree(locations)}
                </TreeView>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300,
                  }}
                >
                  <WarehouseIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    {selectedWarehouse
                      ? t('No locations found. Create your first location.')
                      : t('Please select a warehouse first.')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </CardContent>

      {/* Create/Edit Location Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? t('Edit Location') : t('Create New Location')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('Location Code')}
                name="locationCode"
                value={formData.locationCode}
                onChange={handleInputChange}
                required
                helperText={t('e.g., A-01-02-03')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('Location Name')}
                name="locationName"
                value={formData.locationName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('Location Type')}</InputLabel>
                <Select
                  name="locationType"
                  value={formData.locationType}
                  label={t('Location Type')}
                  onChange={handleInputChange}
                >
                  {locationTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {t(type.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>父級位置 (Parent Location)</InputLabel>
                <Select
                  name="parentId"
                  value={formData.parentId}
                  label="父級位置 (Parent Location)"
                  onChange={handleInputChange}
                >
                  <MenuItem value="">
                    <em>無父級位置 (No Parent)</em>
                  </MenuItem>
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.locationCode} - {location.locationName} ({location.locationType})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('Capacity (CBM)')}
                name="capacity"
                type="number"
                value={formData.capacity}
                onChange={handleInputChange}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('Cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditing ? t('Update') : t('Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default WarehouseLocationManagement; 